/* eslint-disable react-hooks/exhaustive-deps */
import { Helmet } from 'react-helmet';
import { Box, Grid, Pagination } from '@mui/material';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import moment from 'moment';
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import '../scss/Pages/_locHistory.scss';
import customAxios from '../utils/customAxios';
import { useTranslation } from 'react-i18next';
import { GlobalVariables } from '../globalVar';
import { useTranslationHook } from '../hooks/UseTranslationHook';
import LocationLogsList from '../components/Logs/Location/LocationLogsList';
import LocationLogsSettingsCard from '../components/Logs/Location/LocationLogsSettingsCard';

function LocHistory() {
  const [isLoading, setIsLoading] = useState(false);
  const [techs, setTechs] = useState([]);
  const [tech, setTech] = useState();
  const [textSearch, setTextSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const locationsPerPage = 10;
  const [noLocs, setNoLocs] = useState(false);
  const [viewConnections, setViewConnections] = useState(true);
  const [mapCenter, setMapCenter] = useState({
    lat: 25.6878978,
    lng: -100.3138497,
  });
  const [filterDate, setFilterDate] = useState({
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day'),
  });

  useEffect(() => {
    fetchUserMapConfig();
  }, []);

  const { titleTechsString } = useTranslationHook();
  const { t } = useTranslation();

  const {
    techGHHistoryTitle,
    techGHHistorySubtitle,
    techGHHistoryPlaceholder,
    techGHGeolocation,
  } = useTranslationHook();

  const mapRef = useRef(null);

  const fetchUserMapConfig = async () => {
    try {
      const userId = localStorage.getItem('currUserLogged');
      const endpoint = `${GlobalVariables.getURL()}user-config/${userId}`;
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      const mapConfig = response.data.data.modules.Map[0];
      const coords = JSON.parse(mapConfig.value);

      setMapCenter({
        lat: coords[0],
        lng: coords[1],
      });
    } catch (error) {
      console.log('🚀 ~ error:', error);
    }
  };

  const getLocationLogs = async () => {
    var startDate, endDate;

    if (filterDate.startDate !== '')
      startDate = filterDate.startDate.utc().format('YYYY-MM-DD HH:mm:ss');

    if (filterDate.endDate !== '')
      endDate = filterDate.endDate.utc().format('YYYY-MM-DD HH:mm:ss');

    if (tech) {
      const endpoint = `${GlobalVariables.getURL()}techLocLogs/${
        tech.id
      }?start_date=${
        filterDate.startDate !== '' ? encodeURIComponent(startDate) : ''
      }&end_date=${
        filterDate.endDate !== '' ? encodeURIComponent(endDate) : ''
      }&all=${true}`;
      // &page=${page}&per_page=${10}
      setIsLoading(true);

      try {
        const { data } = await customAxios.get(endpoint, {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        });
        const locs = data.data
          .map((locationLog) => {
            return {
              ...locationLog,
              register_at: new Date(locationLog.register_at),
            };
          })
          .sort((a, b) => b.register_at - a.register_at);

        const pageCount = Math.ceil(locs.length / locationsPerPage);
        setLocations(locs);
        setNoLocs(false);
        setIsLoading(false);
        setNumPages(pageCount);
      } catch (error) {
        console.log('🚀 ~ error:', error);
        setLocations([]);
        setNoLocs(true);
        setIsLoading(false);
      }
    } else {
      setLocations([]);
      setNoLocs(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getLocationLogs();
  }, [tech, page]);

  //#region Techs fetch for autocomplete
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      try {
        const response = await customAxios.get(
          `${GlobalVariables.getURL()}listTechs`,
          {
            headers: {
              Authorization: 'bearer ' + localStorage.getItem('token'),
            },
          }
        );
        const techData = response.data.map((tech) => {
          return {
            ...tech,
            is_active: t(`${tech.is_active}`),
          };
        });
        setTechs(techData);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    };

    getData();
  }, [textSearch]);
  //#endregion

  //#region Map Region
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    }),
    []
  );

  const Map = () => {
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    if (!isLoaded) {
      return <></>;
    }

    return (
      <GoogleMap
        ref={mapRef}
        center={mapCenter}
        zoom={5}
        onLoad={onLoad}
        mapContainerClassName='locHistoryMap'
        options={mapOptions}
      >
        {locations.map((marker, index) => (
          <Marker
            key={index}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            title={`${marker.register_at}`}
          />
        ))}
        {viewConnections && (
          <Polyline
            path={locations.map((marker) => ({
              lat: marker.latitude,
              lng: marker.longitude,
            }))}
          />
        )}
      </GoogleMap>
    );
  };
  //#endregion

  return (
    <div className='rootContainer'>
      <Helmet>
        <title>{titleTechsString}</title>
      </Helmet>

      {isLoading && !techs ? (
        <></>
      ) : (
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={moment.locale()}
        >
          <Grid
            container
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
            }}
          >
            <Grid
              item
              xs={12}
              lg={4}
              sx={{ height: { xs: 'auto', lg: '100%' }, width: '100%' }}
            >
              <Box height={'100%'} padding={2}>
                <Box className='titleHistory' height={'5%'}>
                  {techGHHistoryTitle} <DoubleArrowIcon />{' '}
                  <span className='highlight'>{techGHGeolocation}</span>
                </Box>
                <Box className='subtitleHistory' height={'5%'}>
                  {techGHHistorySubtitle}
                </Box>
                <Box className='settingContainer'>
                  <LocationLogsSettingsCard
                    techs={techs}
                    setTech={setTech}
                    setTextSearch={setTextSearch}
                    setFilterDate={setFilterDate}
                    filter={filterDate}
                    onButtonClick={getLocationLogs}
                    viewConnections={viewConnections}
                    setViewConnections={setViewConnections}
                  />
                </Box>

                {!tech ? (
                  <Box className='locationHistoryPlaceholder'>
                    <HistoryOutlinedIcon
                      color='disabled'
                      sx={{ fontSize: '3rem' }}
                    />
                    <Box
                      className='locationHistoryPlaceholderText'
                      color='gray'
                    >
                      {techGHHistoryPlaceholder}
                    </Box>
                  </Box>
                ) : (
                  <Box className='listContainer'>
                    <LocationLogsList
                      locations={locations.slice(
                        (page - 1) * locationsPerPage,
                        page * locationsPerPage
                      )}
                      filter={filterDate}
                      isLoading={isLoading}
                      noLocs={noLocs}
                    />
                    {locations.length > 0 && (
                      <Pagination
                        count={numPages}
                        size='small'
                        page={page}
                        onChange={(event, page) => setPage(page)}
                        sx={{ marginTop: '3%' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              lg={8}
              sx={{ height: { xs: '50vh', lg: '100%' } }}
            >
              <Map />
            </Grid>
          </Grid>
        </LocalizationProvider>
      )}
    </div>
  );
}
export default LocHistory;
